import React from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Grid,
  Typography,
} from "@mui/material";
import { createAxios } from "../../createAxios";
import { getUserId } from "../../utils/authSession";
import {
  useCreateFlashcardCardMutation,
  useCreateFlashcardDeckMutation,
  useDeleteFlashcardCardMutation,
  useDeleteFlashcardDeckMutation,
  useFlashcardCardsQuery,
  useFlashcardWordSuggestionsQuery,
  useLookupFlashcardWordMutation,
  useMyFlashcardDecksQuery,
  usePublicFlashcardDecksQuery,
  useReviewFlashcardCardMutation,
  useUpdateFlashcardDeckMutation,
} from "../../queries/useToeicQueries";
import AddCardDialog from "./components/AddCardDialog";
import CreateDeckDialog from "./components/CreateDeckDialog";
import DeckCardsList from "./components/DeckCardsList";
import DeckLibraryPanel from "./components/DeckLibraryPanel";
import DeckReviewPanel from "./components/DeckReviewPanel";
import PracticeModeDialog from "./components/PracticeModeDialog";
import {
  emptyCardForm,
  emptyDeckForm,
  normalizePracticeAnswer,
  practicePhaseMeta,
  practicePhaseOrder,
  shuffleFlashcards,
} from "./flashcardUtils";

function FlashcardsPage() {
  const [mode, setMode] = React.useState("public");
  const [query, setQuery] = React.useState("");
  const [selectedDeckID, setSelectedDeckID] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isFlipped, setIsFlipped] = React.useState(false);
  const [deckForm, setDeckForm] = React.useState(emptyDeckForm);
  const [cardForm, setCardForm] = React.useState(emptyCardForm);
  const [createDeckOpen, setCreateDeckOpen] = React.useState(false);
  const [addCardOpen, setAddCardOpen] = React.useState(false);
  const [practiceModeOpen, setPracticeModeOpen] = React.useState(false);
  const [practiceCards, setPracticeCards] = React.useState([]);
  const [practicePhase, setPracticePhase] = React.useState("learn");
  const [practiceIndex, setPracticeIndex] = React.useState(0);
  const [practiceFlipped, setPracticeFlipped] = React.useState(false);
  const [practiceAnswer, setPracticeAnswer] = React.useState("");
  const [practiceResult, setPracticeResult] = React.useState(null);
  const [writeMisses, setWriteMisses] = React.useState([]);
  const [practiceStats, setPracticeStats] = React.useState({
    known: 0,
    review: 0,
    typed: 0,
    typos: 0,
  });
  const [wordSuggestionQuery, setWordSuggestionQuery] = React.useState("");

  const currentUser = useSelector((state) => state.user.signin.currentUser);
  const accessToken = currentUser?.metadata?.tokens?.accessToken;
  const userID = getUserId(currentUser);
  const axiosJWT = createAxios(currentUser);
  const isSignedIn = Boolean(currentUser && accessToken && userID);

  const publicDecksQuery = usePublicFlashcardDecksQuery(query);
  const myDecksQuery = useMyFlashcardDecksQuery({
    accessToken,
    userID,
    axiosJWT,
    keySearch: query,
  });

  const decks = mode === "mine" ? myDecksQuery.data || [] : publicDecksQuery.data || [];
  const selectedDeck = decks.find((deck) => deck._id === selectedDeckID) || decks[0];
  const isMineDeck = mode === "mine";
  const useAuthenticatedCards = isMineDeck && isSignedIn;

  const cardsQuery = useFlashcardCardsQuery({
    deckID: selectedDeck?._id,
    accessToken,
    userID,
    axiosJWT,
    isMine: useAuthenticatedCards,
  });

  const cards = React.useMemo(() => cardsQuery.data || [], [cardsQuery.data]);
  const wordSuggestionsQuery = useFlashcardWordSuggestionsQuery(wordSuggestionQuery);
  const activeCard = cards[activeIndex] || cards[0];
  const rememberedCount = cards.filter((card) => card.remembered).length;
  const progress = cards.length ? Math.round((rememberedCount / cards.length) * 100) : 0;
  const hasNextCard = cards.length > 1;
  const practiceDeck = practiceCards.length ? practiceCards : cards;
  const practiceDeckLength = practiceDeck.length;
  const practiceCard = practiceDeck[practiceIndex] || practiceDeck[0];
  const practicePhaseIndex = practicePhaseOrder.indexOf(practicePhase);
  const practiceMeta = practicePhaseMeta[practicePhase] || practicePhaseMeta.learn;
  const isPracticeDone = practicePhase === "done";
  const practiceStep = Math.max(practicePhaseIndex, 0) * Math.max(practiceDeckLength, 1) + Math.min(practiceIndex + 1, Math.max(practiceDeckLength, 1));
  const practiceTotalSteps = practicePhaseOrder.length * Math.max(cards.length, 1);
  const practiceProgress = cards.length
    ? isPracticeDone
      ? 100
      : Math.min(100, Math.round((practiceStep / practiceTotalSteps) * 100))
    : 0;

  const createDeckMutation = useCreateFlashcardDeckMutation();
  const updateDeckMutation = useUpdateFlashcardDeckMutation();
  const deleteDeckMutation = useDeleteFlashcardDeckMutation();
  const createCardMutation = useCreateFlashcardCardMutation();
  const deleteCardMutation = useDeleteFlashcardCardMutation();
  const reviewCardMutation = useReviewFlashcardCardMutation();
  const lookupWordMutation = useLookupFlashcardWordMutation();
  const isReviewingCard = reviewCardMutation.isPending || reviewCardMutation.isLoading;

  React.useEffect(() => {
    const keyword = cardForm.front.trim();
    const timer = window.setTimeout(() => {
      setWordSuggestionQuery(keyword.length >= 2 ? keyword : "");
    }, 260);

    return () => window.clearTimeout(timer);
  }, [cardForm.front]);

  React.useEffect(() => {
    setSelectedDeckID("");
    setActiveIndex(0);
    setIsFlipped(false);
  }, [mode, query]);

  React.useEffect(() => {
    const sharedSlug = new URLSearchParams(window.location.search).get("deck");
    if (!sharedSlug || !publicDecksQuery.data?.length) return;

    const sharedDeck = publicDecksQuery.data.find(
      (deck) => deck.shareSlug === sharedSlug
    );
    if (sharedDeck) {
      setMode("public");
      setSelectedDeckID(sharedDeck._id);
    }
  }, [publicDecksQuery.data]);

  React.useEffect(() => {
    setActiveIndex(0);
    setIsFlipped(false);
    setPracticeModeOpen(false);
    setPracticeCards([]);
    setWriteMisses([]);
  }, [selectedDeck?._id]);

  const resetPracticeSession = React.useCallback(() => {
    setPracticeCards(shuffleFlashcards(cards));
    setPracticePhase("learn");
    setPracticeIndex(0);
    setPracticeFlipped(false);
    setPracticeAnswer("");
    setPracticeResult(null);
    setWriteMisses([]);
    setPracticeStats({
      known: 0,
      review: 0,
      typed: 0,
      typos: 0,
    });
  }, [cards]);

  const authPayload = {
    accessToken,
    userID,
    axiosJWT,
  };

  const applyLexicalEntry = React.useCallback((entry) => {
    if (!entry) return;

    setCardForm((current) => ({
      ...current,
      front: entry.word || current.front,
      back: entry.definition || "",
      example: entry.example || "",
      ipa: entry.ipa || "",
      audioUrl: entry.audioUrl || "",
      audioUrls: entry.audioUrls || (entry.audioUrl ? [entry.audioUrl] : []),
      audioRegion: entry.audioRegion || "",
      partOfSpeech: entry.partOfSpeech || "",
      definition: entry.definition || "",
      lexicalSource: entry.source || "",
    }));
  }, []);

  const lookupWord = React.useCallback(
    async (word) => {
      const keyword = (word || "").trim();
      if (!keyword) return;

      const entry = await lookupWordMutation.mutateAsync(keyword);
      applyLexicalEntry(entry);
    },
    [applyLexicalEntry, lookupWordMutation]
  );

  const updateDeckForm = (field) => (event) => {
    const value = field === "isPublic" ? event.target.checked : event.target.value;
    setDeckForm((current) => ({ ...current, [field]: value }));
  };

  const createDeck = async (event) => {
    event.preventDefault();
    if (!isSignedIn || !deckForm.name.trim()) return;

    const deck = await createDeckMutation.mutateAsync({
      ...authPayload,
      deck: {
        name: deckForm.name.trim(),
        description: deckForm.description.trim(),
        tags: deckForm.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
        isPublic: deckForm.isPublic,
      },
    });

    setDeckForm(emptyDeckForm);
    setCreateDeckOpen(false);
    setMode("mine");
    setSelectedDeckID(deck._id);
  };

  const createCard = async (event) => {
    event.preventDefault();
    if (!selectedDeck?._id || !cardForm.front.trim() || !cardForm.back.trim()) return;

    await createCardMutation.mutateAsync({
      ...authPayload,
      deckID: selectedDeck._id,
      card: {
        front: cardForm.front.trim(),
        back: cardForm.back.trim(),
        example: cardForm.example.trim(),
        ipa: cardForm.ipa.trim(),
        audioUrl: cardForm.audioUrl.trim(),
        audioUrls: cardForm.audioUrls || [],
        audioRegion: cardForm.audioRegion.trim(),
        partOfSpeech: cardForm.partOfSpeech.trim(),
        definition: cardForm.definition.trim(),
        lexicalSource: cardForm.lexicalSource.trim(),
        note: cardForm.note.trim(),
      },
    });

    setCardForm(emptyCardForm);
    setAddCardOpen(false);
    setActiveIndex(0);
    setIsFlipped(false);
  };

  const toggleDeckVisibility = async () => {
    if (!selectedDeck?._id) return;

    await updateDeckMutation.mutateAsync({
      ...authPayload,
      deckID: selectedDeck._id,
      update: { isPublic: !selectedDeck.isPublic },
    });
  };

  const deleteDeck = async () => {
    if (!selectedDeck?._id) return;

    await deleteDeckMutation.mutateAsync({
      ...authPayload,
      deckID: selectedDeck._id,
    });
    setSelectedDeckID("");
  };

  const deleteCard = async (cardID) => {
    await deleteCardMutation.mutateAsync({
      ...authPayload,
      cardID,
    });
    setActiveIndex(0);
    setIsFlipped(false);
  };

  const reviewCard = async (card) => {
    if (!isSignedIn || !card?._id) return;

    await reviewCardMutation.mutateAsync({
      ...authPayload,
      cardID: card._id,
      remembered: !card.remembered,
    });
  };

  const nextCard = () => {
    if (!cards.length) return;
    setActiveIndex((current) => (current + 1) % cards.length);
    setIsFlipped(false);
  };

  const openPracticeMode = () => {
    resetPracticeSession();
    setPracticeModeOpen(true);
  };

  const advancePractice = React.useCallback(() => {
    if (!practiceDeckLength) return;

    setPracticeAnswer("");
    setPracticeResult(null);
    setPracticeFlipped(false);

    setPracticeIndex((currentIndex) => {
      const isLastCard = currentIndex >= practiceDeckLength - 1;
      const missedCard =
        practicePhase === "write" && practiceResult === false && practiceCard
          ? practiceCard
          : null;

      if (!isLastCard) {
        if (missedCard) {
          setWriteMisses((current) => [...current, missedCard]);
        }
        return currentIndex + 1;
      }

      if (practicePhase === "write") {
        const nextMisses = missedCard ? [...writeMisses, missedCard] : writeMisses;

        if (nextMisses.length) {
          setPracticeCards(shuffleFlashcards(nextMisses));
          setWriteMisses([]);
          return 0;
        }

        setPracticePhase("done");
        return 0;
      }

      const currentPhaseIndex = practicePhaseOrder.indexOf(practicePhase);
      const nextPhase = practicePhaseOrder[currentPhaseIndex + 1];
      setPracticePhase(nextPhase || "done");
      setPracticeCards(shuffleFlashcards(cards));
      setWriteMisses([]);

      return 0;
    });
  }, [cards, practiceCard, practiceDeckLength, practicePhase, practiceResult, writeMisses]);

  const handlePracticeRecall = async (remembered) => {
    if (!practiceCard) return;

    setPracticeStats((current) => ({
      ...current,
      known: current.known + (remembered ? 1 : 0),
      review: current.review + (remembered ? 0 : 1),
    }));

    if (isSignedIn && practiceCard._id) {
      await reviewCardMutation.mutateAsync({
        ...authPayload,
        cardID: practiceCard._id,
        remembered,
      });
    }

    advancePractice();
  };

  const checkPracticeAnswer = () => {
    if (!practiceCard) return;

    const isCorrect =
      normalizePracticeAnswer(practiceAnswer) === normalizePracticeAnswer(practiceCard.front);

    setPracticeResult(isCorrect);
    setPracticeStats((current) => ({
      ...current,
      typed: current.typed + 1,
      typos: current.typos + (isCorrect ? 0 : 1),
    }));
  };

  const getPracticeAdvanceLabel = () => {
    if (practicePhase === "write" && practiceResult === false) return "Review later";
    if (practiceIndex < practiceDeckLength - 1) return "Next word";
    if (practicePhase === "learn") return "Start recall";
    if (practicePhase === "recall") return "Start writing";
    if (practicePhase === "write") return "Finish practice";
    return "Continue";
  };

  const copyShareLink = async () => {
    if (!selectedDeck?.shareSlug) return;
    const url = `${window.location.origin}/flashcards?deck=${selectedDeck.shareSlug}`;
    await navigator.clipboard.writeText(url);
  };

  return (
    <Box sx={{ width: "100%", mt: 4, mb: 6 }}>
      <Box component="header" sx={{ maxWidth: 880 }}>
        <Typography
          component="h1"
          sx={{
            m: 0,
            color: "var(--toeic-ink)",
            fontSize: { xs: 36, md: 56 },
            fontWeight: 800,
            letterSpacing: 0,
            lineHeight: { xs: 1.05, md: 1 },
          }}
        >
          <Box component="span" sx={{ position: "relative", display: "inline-block" }}>
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                position: "absolute",
                left: "-0.12em",
                right: "-0.12em",
                bottom: "0.04em",
                height: "0.44em",
                bgcolor: "rgba(109, 74, 255, 0.2)",
                borderRadius: "0.45em 0.6em 0.4em 0.55em",
                transform: "rotate(-1deg)",
              }}
            />
            <Box component="span" sx={{ position: "relative" }}>
              Flashcards
            </Box>
          </Box>
          .
        </Typography>
        <Typography
          className="toeic-reveal"
          sx={{
            mt: 2,
            maxWidth: 680,
            color: "var(--toeic-ink-soft)",
            fontSize: 18,
            fontWeight: 600,
            lineHeight: 1.65,
          }}
        >
          Create vocabulary decks, review cards, and publish decks so other
          learners can study from them too.
        </Typography>
      </Box>

      <Grid container spacing={2.5} sx={{ mt: 3 }}>
        <Grid item xs={12} lg={3}>
          <DeckLibraryPanel
            decks={decks}
            mode={mode}
            query={query}
            selectedDeck={selectedDeck}
            isSignedIn={isSignedIn}
            onModeChange={setMode}
            onQueryChange={setQuery}
            onSelectDeck={setSelectedDeckID}
            onCreateDeck={() => setCreateDeckOpen(true)}
          />
        </Grid>
        <Grid item xs={12} lg={9}>
          <DeckReviewPanel
            selectedDeck={selectedDeck}
            activeCard={activeCard}
            cards={cards}
            progress={progress}
            isFlipped={isFlipped}
            isMineDeck={isMineDeck}
            isSignedIn={isSignedIn}
            hasNextCard={hasNextCard}
            onFlipCard={() => setIsFlipped((current) => !current)}
            onAddCard={() => setAddCardOpen(true)}
            onToggleDeckVisibility={toggleDeckVisibility}
            onCopyShareLink={copyShareLink}
            onDeleteDeck={deleteDeck}
            onOpenPractice={openPracticeMode}
            onNextCard={nextCard}
            onReviewCard={reviewCard}
            onDeleteCard={deleteCard}
          />
          <PracticeModeDialog
            open={practiceModeOpen}
            cards={cards}
            practiceCards={practiceDeck}
            selectedDeck={selectedDeck}
            practiceCard={practiceCard}
            practicePhase={practicePhase}
            practicePhaseIndex={practicePhaseIndex}
            practiceMeta={practiceMeta}
            practiceIndex={practiceIndex}
            practiceProgress={practiceProgress}
            practiceStats={practiceStats}
            practiceFlipped={practiceFlipped}
            practiceAnswer={practiceAnswer}
            practiceResult={practiceResult}
            isPracticeDone={isPracticeDone}
            isReviewingCard={isReviewingCard}
            onClose={() => setPracticeModeOpen(false)}
            onResetPractice={resetPracticeSession}
            onAdvancePractice={advancePractice}
            onPracticeRecall={handlePracticeRecall}
            onCheckPracticeAnswer={checkPracticeAnswer}
            onGetAdvanceLabel={getPracticeAdvanceLabel}
            onSetPracticeFlipped={setPracticeFlipped}
            onSetPracticeAnswer={setPracticeAnswer}
            onSetPracticeResult={setPracticeResult}
          />
          <CreateDeckDialog
            open={createDeckOpen}
            deckForm={deckForm}
            onClose={() => setCreateDeckOpen(false)}
            onCreateDeck={createDeck}
            onUpdateDeckForm={updateDeckForm}
          />
          {isMineDeck && selectedDeck && (
            <AddCardDialog
              open={addCardOpen}
              selectedDeck={selectedDeck}
              cardForm={cardForm}
              wordSuggestionsQuery={wordSuggestionsQuery}
              lookupWordMutation={lookupWordMutation}
              onClose={() => setAddCardOpen(false)}
              onCreateCard={createCard}
              onLookupWord={lookupWord}
              onSetCardForm={setCardForm}
            />
          )}
          {selectedDeck && (
            <DeckCardsList
              cards={cards}
              activeIndex={activeIndex}
              isSignedIn={isSignedIn}
              onSelectCard={(index) => {
                setActiveIndex(index);
                setIsFlipped(false);
              }}
              onReviewCard={reviewCard}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default FlashcardsPage;
